import { create } from '@thoughtspot/logger';
import { handleMuzeAiRequest } from './index';
const logger = create('chart-to-ts > muze-ai-host-bridge');
const MSG_FROM_CHART = 'muze-studio-ai';
const MSG_FROM_HOST = 'muze-studio-ai-host';
const TEMPLATE_BUILDER_API_TYPES = [
    'template-builder-open',
    'template-builder-result',
    'template-builder-error',
];
const NO_TEMPLATE_BUILDER_ERROR = 'Template builder is not available in this context.';
const BAD_TEMPLATE_BUILDER_PAYLOAD_ERROR = 'Malformed template-builder payload.';
function isTemplateBuilderApiType(apiType) {
    return TEMPLATE_BUILDER_API_TYPES.includes(apiType);
}
function toErrorMessage(err) {
    return err instanceof Error ? err.message : String(err);
}
export function setupMuzeAiHostBridge(opts) {
    const { chartIframeElRef, chartFrameOrigin, onTemplateBuilderEventRef } = opts;
    const postToChart = (message, targetOrigin) => {
        chartIframeElRef.current?.contentWindow?.postMessage(message, targetOrigin || '*');
    };
    const replyToChart = (requestId, replyOrigin, result) => {
        postToChart({ source: MSG_FROM_HOST, kind: 'result', requestId, ...result }, replyOrigin);
    };
    const handleTemplateBuilderRequest = (requestId, apiType, body, replyOrigin) => {
        const handler = onTemplateBuilderEventRef?.current;
        if (!handler) {
            replyToChart(requestId, replyOrigin, { ok: false, error: NO_TEMPLATE_BUILDER_ERROR });
            return;
        }
        let payload;
        try {
            payload = typeof body === 'string' && body.length > 0 ? JSON.parse(body) : undefined;
        }
        catch {
            replyToChart(requestId, replyOrigin, { ok: false, error: BAD_TEMPLATE_BUILDER_PAYLOAD_ERROR });
            return;
        }
        Promise.resolve(handler({ type: apiType, payload }))
            .then(outcome => replyToChart(requestId, replyOrigin, { ok: true, data: outcome ?? { ok: true } }))
            .catch(err => replyToChart(requestId, replyOrigin, { ok: false, error: toErrorMessage(err) }));
    };
    const onMessage = (event) => {
        const data = event.data;
        if (!data || data.source !== MSG_FROM_CHART)
            return;
        if (event.source !== chartIframeElRef.current?.contentWindow)
            return;
        if (chartFrameOrigin && event.origin !== chartFrameOrigin) {
            logger.warn('muze-ai: dropping message from unexpected origin', {
                got: event.origin,
                expected: chartFrameOrigin,
            });
            return;
        }
        const replyOrigin = event.origin || chartFrameOrigin || '*';
        if (data.kind === 'hello') {
            postToChart({ source: MSG_FROM_HOST, kind: 'ready' }, replyOrigin);
            return;
        }
        if (data.kind === 'request') {
            const { requestId, apiType, sessionId, body, convKey } = data;
            if (isTemplateBuilderApiType(apiType)) {
                handleTemplateBuilderRequest(requestId, apiType, body, replyOrigin);
                return;
            }
            const onChunk = (chunk) => postToChart({ source: MSG_FROM_HOST, kind: 'chunk', requestId, chunk }, replyOrigin);
            handleMuzeAiRequest({ apiType, sessionId, body, convKey }, { onChunk })
                .then(res => {
                if (res.isValid) {
                    return replyToChart(requestId, replyOrigin, { ok: true, data: res.data });
                }
                return replyToChart(requestId, replyOrigin, {
                    ok: false,
                    error: res.errors?.[0] ?? 'Unknown error',
                });
            })
                .catch(err => {
                replyToChart(requestId, replyOrigin, { ok: false, error: toErrorMessage(err) });
            });
        }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}
//# sourceMappingURL=host-bridge.js.map